import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Carousel } from 'react-bootstrap';

function Clients() {
  const [clientsData, setClientsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchClientsData();
  }, []);

  const fetchClientsData = async () => {
    try {
      const response = await fetch(`${baseURL}/clients.php`);
      if (response.ok) {
        const data = await response.json();
        setClientsData(data);
      } else {
        throw new Error('Failed to fetch clients data');
      }
    } catch (err) {
      setError('Error loading clients. Please try again later.');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading clients...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <div className="text-center">
          <button onClick={fetchClientsData} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </Container>
    );
  }

  const clients = clientsData?.clients || [];
  const testimonials = clientsData?.testimonials || [];

  return (
    <Container className="py-5">
      <Row className="mb-5">
        <Col>
          <h1 className="text-center royal-blue-text emerald-green-border mb-4">Our Valued Clients</h1>
          <p className="lead text-center">
            We are proud to partner with leading institutions and industries across East Africa, 
            providing them with reliable laboratory solutions and exceptional service.
          </p>
        </Col>
      </Row>

      {/* Clients Grid */}
      <Row className="mb-5">
        {clients.length > 0 ? (
          clients.map((client) => (
            <Col lg={4} md={6} className="mb-4" key={client.id}>
              <Card className="service-card h-100">
                <Card.Body className="text-center p-4">
                  <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                       style={{width: '80px', height: '80px'}}>
                    <i className="fas fa-building fa-2x royal-blue-text"></i>
                  </div>
                  <Card.Title className="royal-blue-text">{client.name}</Card.Title>
                  <Card.Text className="mb-0">{client.description}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          // Fallback clients if database is empty
          [
            {
              id: 1,
              name: "Tea Board of Kenya",
              description: "Providing analytical solutions for tea quality testing and research."
            },
            {
              id: 2,
              name: "KIRDI",
              description: "Kenya Industrial Research and Development Institute - supporting industrial research with advanced laboratory equipment."
            },
            {
              id: 3,
              name: "Sukari Industries",
              description: "Sugar processing quality control and analytical testing solutions."
            },
            {
              id: 4,
              name: "Brookside Dairies",
              description: "Dairy product quality assurance and testing equipment."
            },
            {
              id: 5,
              name: "USIU",
              description: "United States International University - supporting academic research and education."
            },
            {
              id: 6,
              name: "University of Nairobi",
              description: "Equipping one of Kenya's premier universities with research-grade laboratory instruments."
            }
          ].map((client) => (
            <Col lg={4} md={6} className="mb-4" key={client.id}>
              <Card className="service-card h-100">
                <Card.Body className="text-center p-4">
                  <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                       style={{width: '80px', height: '80px'}}>
                    <i className="fas fa-building fa-2x royal-blue-text"></i>
                  </div>
                  <Card.Title className="royal-blue-text">{client.name}</Card.Title>
                  <Card.Text className="mb-0">{client.description}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <Row className="mt-5">
          <Col>
            <Card className="border-0 bg-light">
              <Card.Body className="p-5">
                <h3 className="royal-blue-text text-center mb-4">What Our Clients Say</h3>
                <Carousel indicators={false} controls={testimonials.length > 1}>
                  {testimonials.map((testimonial, index) => (
                    <Carousel.Item key={testimonial.id}>
                      <div className="text-center">
                        <div className="mb-3">
                          {[...Array(5)].map((_, i) => (
                            <i 
                              key={i} 
                              className={`fas fa-star ${i < testimonial.rating ? 'text-warning' : 'text-muted'}`}
                            ></i>
                          ))}
                        </div>
                        <blockquote className="blockquote mb-4">
                          <p className="fs-5 fst-italic">"{testimonial.testimonial_text}"</p>
                        </blockquote>
                        <footer className="blockquote-footer mt-4">
                          <strong>{testimonial.client_name}</strong>
                          {testimonial.client_position && `, ${testimonial.client_position}`}
                          {testimonial.client_company && (
                            <cite title={testimonial.client_company}> at {testimonial.client_company}</cite>
                          )}
                        </footer>
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default Clients;